const ALLOWED_FILE_TYPES = new Set(['image/jpeg', 'image/png']);
const ALLOWED_FILE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);

class UTFIPersonalization extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    const form = document.getElementById(this.dataset.formId);
    if (!(form instanceof HTMLFormElement)) return;

    this.dataset.initialized = 'true';
    this.form = form;
    this.originalNoValidate = form.noValidate;
    form.noValidate = true;
    this.form.addEventListener('submit', this.handleSubmit, true);
    this.addEventListener('input', this.handleInput);
    this.addEventListener('change', this.handleInput);
    this.updateCharacterCount();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit, true);
    if (this.form) this.form.noValidate = this.originalNoValidate;
    this.removeEventListener('input', this.handleInput);
    this.removeEventListener('change', this.handleInput);
  }

  handleInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) return;
    this.clearError(field);
    this.updateCharacterCount();
  };

  handleSubmit = (event) => {
    const fields = [...this.querySelectorAll('input, select, textarea')];
    let firstInvalid;
    const messages = [];

    for (const field of fields) {
      if (field instanceof HTMLInputElement && field.type !== 'file') field.value = field.value.trim();
      if (field instanceof HTMLTextAreaElement) field.value = field.value.trim();

      const message = this.validateField(field);
      if (message) {
        this.showError(field, message);
        messages.push(`${this.getLabel(field)}: ${message}`);
        firstInvalid ||= field;
      } else {
        this.clearError(field);
      }
    }

    if (!firstInvalid) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const summary = this.querySelector('.utfi-personalization__summary');
    summary.hidden = false;
    summary.textContent = `Please correct ${messages.length} field${messages.length === 1 ? '' : 's'}: ${messages.join(' ')}`;
    firstInvalid.focus();
  };

  validateField(field) {
    if (field.required && field instanceof HTMLInputElement && field.type === 'file' && field.files.length === 0) return 'Choose a file.';
    if (field.required && field.value.trim() === '') return 'This field is required.';
    if (field instanceof HTMLInputElement && field.type === 'file' && field.files.length) {
      const file = field.files[0];
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      if (!ALLOWED_FILE_TYPES.has(file.type) && !ALLOWED_FILE_EXTENSIONS.has(extension)) return 'Choose a JPG, JPEG, or PNG file.';
    }
    if (!field.checkValidity()) return field.validationMessage;
    return '';
  }

  showError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    const errorId = field.getAttribute('aria-describedby')?.split(' ').find((id) => id.endsWith('-error'));
    const error = errorId && document.getElementById(errorId);
    if (error) error.textContent = message;
  }

  clearError(field) {
    field.removeAttribute('aria-invalid');
    const errorId = field.getAttribute('aria-describedby')?.split(' ').find((id) => id.endsWith('-error'));
    const error = errorId && document.getElementById(errorId);
    if (error) error.textContent = '';
    const summary = this.querySelector('.utfi-personalization__summary');
    if (summary) summary.hidden = true;
  }

  getLabel(field) {
    return this.querySelector(`label[for="${CSS.escape(field.id)}"]`)?.textContent.replace('*', '').trim() || 'Field';
  }

  updateCharacterCount() {
    const textarea = this.querySelector('textarea[maxlength]');
    const count = this.querySelector('.utfi-personalization__count');
    if (textarea && count) count.textContent = `${textarea.value.length} / ${textarea.maxLength}`;
  }
}

if (!customElements.get('utfi-personalization')) customElements.define('utfi-personalization', UTFIPersonalization);
